import * as Sentry from '@sentry/react'

// Keyname-based scrubbing — any dict value whose key matches one of these
// substrings is replaced with `<scrubbed>` regardless of content.
const SENSITIVE_KEY_REGEX =
  /(phone|email|recording_url|transcript|webhook_secret|auth_token|api_key|secret_key|pat_hash|session_secret)/i

const SCRUBBED = '<scrubbed>'

// Value-based PII patterns. Applied to every string we walk, so they catch
// phone numbers / emails embedded in URL paths, exception messages, frame
// vars, breadcrumb messages, and click-target text. The placeholders
// (`<phone>`, `<email>`) intentionally contain no digits or `@`, so the
// scrubber is idempotent.
//
// Phone: strict E.164 — the leading `+` is REQUIRED. Without it the regex
// matches Linux thread IDs (15 digits), ms-epoch timestamps (13 digits),
// large numeric correlation/row IDs, etc. The codebase normalizes user
// phones to E.164 (always `+` prefixed) before they ever leave the client.
const PHONE_REGEX = /\+\d{10,15}/g
const EMAIL_REGEX = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g

function scrubString(value: string): string {
  // Email first — phone regex would otherwise eat digits inside an email's
  // local part if the domain starts with digits.
  return value.replace(EMAIL_REGEX, '<email>').replace(PHONE_REGEX, '<phone>')
}

function isSensitiveKey(key: unknown): boolean {
  return typeof key === 'string' && SENSITIVE_KEY_REGEX.test(key)
}

function scrubValue(value: unknown): unknown {
  if (typeof value === 'string') return scrubString(value)
  if (Array.isArray(value)) return value.map(scrubValue)
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = isSensitiveKey(k) ? SCRUBBED : scrubValue(v)
    }
    return out
  }
  return value
}

function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  // 1. Dict-shaped sections — scrubValue handles both keyname and string
  //    regex during the walk.
  for (const top of ['extra', 'contexts', 'request', 'tags', 'user'] as const) {
    const section = (event as unknown as Record<string, unknown>)[top]
    if (section && typeof section === 'object') {
      ;(event as unknown as Record<string, unknown>)[top] = scrubValue(section)
    }
  }

  // 2. Top-level message string.
  if (typeof event.message === 'string') {
    event.message = scrubString(event.message)
  }

  // 3. Exception payload — message string + frame locals.
  const exceptionValues = event.exception?.values
  if (Array.isArray(exceptionValues)) {
    for (const exc of exceptionValues) {
      if (typeof exc.value === 'string') {
        exc.value = scrubString(exc.value)
      }
      const frames = exc.stacktrace?.frames
      if (Array.isArray(frames)) {
        for (const frame of frames) {
          const vars = (frame as unknown as Record<string, unknown>).vars
          if (vars && typeof vars === 'object') {
            ;(frame as unknown as Record<string, unknown>).vars = scrubValue(vars)
          }
        }
      }
    }
  }

  // 4. Request URL + query string. `request` was already walked above as a
  //    dict, but call `scrubString` directly here so the typed union shape
  //    is preserved instead of relying on the generic walk's `unknown`.
  const request = event.request as
    | { url?: string; query_string?: unknown; [k: string]: unknown }
    | undefined
  if (request) {
    if (typeof request.url === 'string') request.url = scrubString(request.url)
    if (typeof request.query_string === 'string') {
      request.query_string = scrubString(request.query_string)
    }
  }

  // 5. Breadcrumbs — both data and message strings.
  if (event.breadcrumbs) {
    for (const crumb of event.breadcrumbs) {
      if (crumb.data && typeof crumb.data === 'object') {
        crumb.data = scrubValue(crumb.data) as Record<string, unknown>
      }
      if (typeof crumb.message === 'string') {
        crumb.message = scrubString(crumb.message)
      }
    }
  }

  return event
}

// DSN is embedded in the public client bundle, so anyone reading page source
// can spam the ingest endpoint with fake events. `allowUrls` filters by the
// script URL inside each stack frame, which the browser populates from the
// real loaded script — events whose frames don't match are dropped before
// sending. Skip the filter in dev so localhost errors aren't muted when a
// dev happens to set VITE_SENTRY_DSN_FRONTEND for testing.
const ALLOWED_SCRIPT_HOSTS: RegExp[] = [
  /https:\/\/app\.voysi\.ai/,
]

function resolveEnvironment(): string {
  // Every `pnpm build` artifact has MODE=production, so MODE can't tell a
  // Railway per-PR preview deploy from prod — derive it from the deploy host
  // at runtime instead. Dev/localhost (non-PROD builds) keep the build mode.
  // Collapsing this to `PROD ? 'production' : MODE` would mislabel every
  // preview deploy as production and pollute prod Sentry data.
  if (!import.meta.env.PROD) return import.meta.env.MODE
  const host = window.location.hostname
  return host === 'app.voysi.ai' ? 'production' : 'preview'
}

export function initSentry(): boolean {
  const dsn = import.meta.env.VITE_SENTRY_DSN_FRONTEND
  if (!dsn) return false

  // Build-time release tag, set in Docker stage 1 from RAILWAY_GIT_COMMIT_SHA.
  // Empty / missing falls back to `undefined` so Sentry tags events with the
  // `release` it sees in the bundled metadata (or none at all in dev).
  const release = import.meta.env.VITE_RELEASE || undefined

  Sentry.init({
    dsn,
    environment: resolveEnvironment(),
    release,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    allowUrls: import.meta.env.PROD ? ALLOWED_SCRIPT_HOSTS : undefined,
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend: scrubEvent,
    beforeBreadcrumb(crumb) {
      if (crumb.data && typeof crumb.data === 'object') {
        crumb.data = scrubValue(crumb.data) as Record<string, unknown>
      }
      if (typeof crumb.message === 'string') {
        crumb.message = scrubString(crumb.message)
      }
      return crumb
    },
  })
  return true
}
